import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import CategoryFormModal from './components/CategoryFormModal';
import ConfirmModal from './components/ConfirmModal';

interface Category {
  id: number;
  name: string;
  sort: number;
  isEnabled: boolean;
  parentId: number | null;
  productCount: number;
  children?: Category[];
}

export default function ProductCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([
    {
      id: 1,
      name: '云电脑',
      sort: 1,
      isEnabled: true,
      parentId: null,
      productCount: 3,
      children: [
        { id: 11, name: '标准型', sort: 1, isEnabled: true, parentId: 1, productCount: 2 },
        { id: 12, name: '高性能型', sort: 2, isEnabled: true, parentId: 1, productCount: 1 },
      ],
    },
    {
      id: 2,
      name: 'GPU服务器',
      sort: 2,
      isEnabled: true,
      parentId: null,
      productCount: 2,
      children: [
        { id: 21, name: 'AI训练', sort: 1, isEnabled: true, parentId: 2, productCount: 1 },
        { id: 22, name: '图形渲染', sort: 2, isEnabled: true, parentId: 2, productCount: 1 },
      ],
    },
    {
      id: 3,
      name: 'PC Farm',
      sort: 3,
      isEnabled: true,
      parentId: null,
      productCount: 1,
    },
    {
      id: 4,
      name: 'AIGC',
      sort: 4,
      isEnabled: true,
      parentId: null,
      productCount: 1,
      children: [
        { id: 41, name: '文本生成', sort: 1, isEnabled: true, parentId: 4, productCount: 0 },
        { id: 42, name: '图像生成', sort: 2, isEnabled: true, parentId: 4, productCount: 1 },
      ],
    },
    {
      id: 5,
      name: '数据盘',
      sort: 5,
      isEnabled: false,
      parentId: null,
      productCount: 0,
    },
  ]);

  const [searchName, setSearchName] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [parentCategory, setParentCategory] = useState<Category | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'batchEnable' | 'batchDisable' | 'batchDelete';
    id?: number;
  } | null>(null);

  const getAllCategories = (): Category[] => {
    const result: Category[] = [];
    categories.forEach((cat) => {
      result.push(cat);
      if (cat.children) {
        result.push(...cat.children);
      }
    });
    return result;
  };

  const filteredCategories = categories.filter((cat) => {
    if (searchName && !cat.name.toLowerCase().includes(searchName.toLowerCase())) {
      return false;
    }
    if (statusFilter === 'enabled' && !cat.isEnabled) return false;
    if (statusFilter === 'disabled' && cat.isEnabled) return false;
    return true;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = getAllCategories().map((c) => c.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };

  const handleSortChange = (id: number, sort: number) => {
    const updateSort = (cats: Category[]): Category[] => {
      return cats.map((cat) => {
        if (cat.id === id) {
          return { ...cat, sort };
        }
        if (cat.children) {
          return { ...cat, children: updateSort(cat.children) };
        }
        return cat;
      });
    };
    setCategories(updateSort(categories));
  };

  const handleToggleStatus = (id: number, isEnabled: boolean) => {
    const updateStatus = (cats: Category[]): Category[] => {
      return cats.map((cat) => {
        if (cat.id === id) {
          return { ...cat, isEnabled };
        }
        if (cat.children) {
          return { ...cat, children: updateStatus(cat.children) };
        }
        return cat;
      });
    };
    setCategories(updateStatus(categories));
  };

  const handleDelete = (id: number) => {
    const category = getAllCategories().find((c) => c.id === id);
    if (category && category.productCount > 0) {
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      toast.textContent = '该分类下存在关联商品，无法删除';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      return;
    }

    setConfirmAction({ type: 'delete', id });
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (!confirmAction?.id) return;

    const deleteCategory = (cats: Category[]): Category[] => {
      return cats
        .filter((cat) => cat.id !== confirmAction.id)
        .map((cat) => {
          if (cat.children) {
            return { ...cat, children: deleteCategory(cat.children) };
          }
          return cat;
        });
    };

    setCategories(deleteCategory(categories));
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleBatchAction = (type: 'enable' | 'disable' | 'delete') => {
    if (selectedIds.length === 0) {
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-amber-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      toast.textContent = '请先选择要操作的分类';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      return;
    }

    if (type === 'delete') {
      const hasProducts = selectedIds.some((id) => {
        const cat = getAllCategories().find((c) => c.id === id);
        return cat && cat.productCount > 0;
      });

      if (hasProducts) {
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        toast.textContent = '选中的分类中存在关联商品，无法删除';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 2000);
        return;
      }
    }

    setConfirmAction({
      type: type === 'enable' ? 'batchEnable' : type === 'disable' ? 'batchDisable' : 'batchDelete',
    });
    setShowConfirmModal(true);
  };

  const handleConfirmBatchAction = () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'batchEnable' || confirmAction.type === 'batchDisable') {
      const isEnabled = confirmAction.type === 'batchEnable';
      const updateStatus = (cats: Category[]): Category[] => {
        return cats.map((cat) => {
          if (selectedIds.includes(cat.id)) {
            return { ...cat, isEnabled };
          }
          if (cat.children) {
            return { ...cat, children: updateStatus(cat.children) };
          }
          return cat;
        });
      };
      setCategories(updateStatus(categories));
    } else if (confirmAction.type === 'batchDelete') {
      const deleteCategories = (cats: Category[]): Category[] => {
        return cats
          .filter((cat) => !selectedIds.includes(cat.id))
          .map((cat) => {
            if (cat.children) {
              return { ...cat, children: deleteCategories(cat.children) };
            }
            return cat;
          });
      };
      setCategories(deleteCategories(categories));
    }

    setSelectedIds([]);
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleSaveCategory = (data: Partial<Category>) => {
    if (editingCategory) {
      const updateCategory = (cats: Category[]): Category[] => {
        return cats.map((cat) => {
          if (cat.id === editingCategory.id) {
            return { ...cat, ...data };
          }
          if (cat.children) {
            return { ...cat, children: updateCategory(cat.children) };
          }
          return cat;
        });
      };
      setCategories(updateCategory(categories));
    } else {
      const newCategory: Category = {
        id: Math.max(0, ...getAllCategories().map((c) => c.id)) + 1,
        name: data.name!,
        sort: data.sort!,
        isEnabled: data.isEnabled!,
        parentId: parentCategory?.id || null,
        productCount: 0,
      };

      if (parentCategory) {
        const addChild = (cats: Category[]): Category[] => {
          return cats.map((cat) => {
            if (cat.id === parentCategory.id) {
              return {
                ...cat,
                children: [...(cat.children || []), newCategory],
              };
            }
            return cat;
          });
        };
        setCategories(addChild(categories));
      } else {
        setCategories([...categories, newCategory]);
      }
    }

    setShowFormModal(false);
    setEditingCategory(null);
    setParentCategory(null);
  };

  const getConfirmMessage = () => {
    if (!confirmAction) return '';
    switch (confirmAction.type) {
      case 'delete':
        return '确定要删除该分类吗？';
      case 'batchEnable':
        return `确定要启用选中的 ${selectedIds.length} 个分类吗？`;
      case 'batchDisable':
        return `确定要禁用选中的 ${selectedIds.length} 个分类吗？`;
      case 'batchDelete':
        return `确定要删除选中的 ${selectedIds.length} 个分类吗？`;
      default:
        return '';
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">商品分类</h1>
            <p className="text-sm text-gray-500 mt-1">管理商品分类信息</p>
          </div>
          <button
            onClick={() => {
              setEditingCategory(null);
              setParentCategory(null);
              setShowFormModal(true);
            }}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            新建分类
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="搜索分类名称"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">全部状态</option>
                <option value="enabled">正常</option>
                <option value="disabled">禁用</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleBatchAction('enable')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                批量启用
              </button>
              <button
                onClick={() => handleBatchAction('disable')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                批量禁用
              </button>
              <button
                onClick={() => handleBatchAction('delete')}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                批量删除
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === getAllCategories().length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    排序
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    分类ID
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    分类名称
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    商品数量
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    分类状态
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <>
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(category.id)}
                          onChange={(e) => handleSelectOne(category.id, e.target.checked)}
                          className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={category.sort}
                          onChange={(e) => handleSortChange(category.id, Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{category.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {category.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {category.productCount}
                      </td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={category.isEnabled}
                            onChange={(e) => handleToggleStatus(category.id, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-500"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3">
                          {!category.children && (
                            <button
                              onClick={() => {
                                setParentCategory(category);
                                setEditingCategory(null);
                                setShowFormModal(true);
                              }}
                              className="text-teal-600 hover:text-teal-700 text-sm whitespace-nowrap"
                            >
                              添加下级
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditingCategory(category);
                              setParentCategory(null);
                              setShowFormModal(true);
                            }}
                            className="text-teal-600 hover:text-teal-700 text-sm whitespace-nowrap"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                    {category.children?.map((child) => (
                      <tr key={child.id} className="hover:bg-gray-50 bg-gray-50/50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(child.id)}
                            onChange={(e) => handleSelectOne(child.id, e.target.checked)}
                            className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={child.sort}
                            onChange={(e) => handleSortChange(child.id, Number(e.target.value))}
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{child.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="ml-6">└─ {child.name}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{child.productCount}</td>
                        <td className="px-4 py-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={child.isEnabled}
                              onChange={(e) => handleToggleStatus(child.id, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-500"></div>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                setEditingCategory(child);
                                setParentCategory(null);
                                setShowFormModal(true);
                              }}
                              className="text-teal-600 hover:text-teal-700 text-sm whitespace-nowrap"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleDelete(child.id)}
                              className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CategoryFormModal
        isOpen={showFormModal}
        category={editingCategory}
        parentCategory={parentCategory}
        onClose={() => {
          setShowFormModal(false);
          setEditingCategory(null);
          setParentCategory(null);
        }}
        onSave={handleSaveCategory}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        message={getConfirmMessage()}
        onConfirm={
          confirmAction?.type === 'delete' ? handleConfirmDelete : handleConfirmBatchAction
        }
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
      />
    </MainLayout>
  );
}
